import React from "react";
import { tierColor } from "../../lib/countries";

/**
 * Semi-circular speedometer gauge for the overall exposure score.
 * - 10 colored segments: green (1-3) → amber (4-6) → orange (7-8) → red (9-10)
 * - Needle indicating the score
 * - Score number displayed in a boxed label below the dial
 */
const SEG_COLORS = (i) => {
  // i = 0..9 representing scores 1..10
  if (i < 3) return ["#2E7D32", "#388E3C", "#7CB342"][i]; // greens
  if (i < 6) return ["#C0CA33", "#D4A017", "#D49A36"][i - 3]; // yellow→amber
  if (i < 8) return ["#E07B1E", "#D84315"][i - 6]; // oranges
  return ["#C62828", "#B71C1C"][i - 8]; // reds
};

const polarToXY = (cx, cy, r, deg) => {
  // 0deg = leftmost point, 90deg = top, 180deg = rightmost point
  const rad = (Math.PI * (180 - deg)) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
};

const arcPath = (cx, cy, rOuter, rInner, startDeg, endDeg) => {
  const a = polarToXY(cx, cy, rOuter, startDeg);
  const b = polarToXY(cx, cy, rOuter, endDeg);
  const c = polarToXY(cx, cy, rInner, endDeg);
  const d = polarToXY(cx, cy, rInner, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${a.x} ${a.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${b.x} ${b.y}`,
    `L ${c.x} ${c.y}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${d.x} ${d.y}`,
    "Z",
  ].join(" ");
};

export const ExposureGauge = ({ score = 0, tier = "Low", size = 320 }) => {
  const safeScore = Math.max(0, Math.min(10, Number(score) || 0));
  const width = size;
  const height = size * 0.72; // semi-circle + label
  const cx = width / 2;
  const cy = size * 0.58;
  const rOuter = size * 0.44;
  const rInner = size * 0.30;
  const tipColor = tierColor(tier);

  // Needle: angle 0 (score 0) → leftmost; 180 (score 10) → rightmost
  const needleDeg = (safeScore / 10) * 180;
  const needleTip = polarToXY(cx, cy, rOuter * 0.96, needleDeg);
  // Base of needle - perpendicular to needle direction for a tapered look
  const baseRad = (Math.PI * (180 - needleDeg)) / 180;
  const baseHalfWidth = 6;
  const baseLeft = {
    x: cx + baseHalfWidth * Math.cos(baseRad - Math.PI / 2),
    y: cy - baseHalfWidth * Math.sin(baseRad - Math.PI / 2),
  };
  const baseRight = {
    x: cx + baseHalfWidth * Math.cos(baseRad + Math.PI / 2),
    y: cy - baseHalfWidth * Math.sin(baseRad + Math.PI / 2),
  };

  return (
    <div
      className="inline-flex flex-col items-center"
      style={{ width, gap: size * 0.04 }}
      data-testid="exposure-gauge"
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-label={`Exposure score ${safeScore.toFixed(1)} out of 10`}
      >
        {/* Coloured segments */}
        {Array.from({ length: 10 }, (_, i) => {
          const start = i * 18;
          const end = start + 18 - 1.5; // small gap between segments
          return (
            <path
              key={i}
              d={arcPath(cx, cy, rOuter, rInner, start, end)}
              fill={SEG_COLORS(i)}
            />
          );
        })}

        {/* Tick marks at 1..9 positions */}
        {Array.from({ length: 11 }, (_, i) => {
          const deg = i * 18;
          const a = polarToXY(cx, cy, rInner - 4, deg);
          const b = polarToXY(cx, cy, rInner - 12, deg);
          return (
            <line
              key={`t-${i}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke="#190F08"
              strokeWidth="1"
              opacity="0.4"
            />
          );
        })}

        {/* Needle */}
        <polygon
          points={`${baseLeft.x},${baseLeft.y} ${baseRight.x},${baseRight.y} ${needleTip.x},${needleTip.y}`}
          fill="#190F08"
        />
        {/* Hub */}
        <circle cx={cx} cy={cy} r={size * 0.04} fill="#190F08" />
        <circle cx={cx} cy={cy} r={size * 0.025} fill={tipColor} />
      </svg>

      {/* Score box label */}
      <div
        className="border-2 border-[#190F08] bg-[#FAF5EE] px-6 py-2 inline-flex items-baseline gap-2"
        data-testid="gauge-score-box"
      >
        <span
          className="font-heading tabular-nums"
          style={{ fontSize: size * 0.13, lineHeight: 1, color: tipColor }}
          data-testid="gauge-score"
        >
          {safeScore.toFixed(1)}
        </span>
        <span
          className="font-heading text-[#190F08]/60"
          style={{ fontSize: size * 0.06, letterSpacing: "0.15em" }}
        >
          / 10
        </span>
      </div>

      <div
        className="font-heading text-xs tracking-[0.25em] uppercase mt-1"
        style={{ color: tipColor }}
        data-testid="gauge-tier"
      >
        {tier} Exposure
      </div>
    </div>
  );
};
